import { useContext, useState, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { styled } from '@mui/system';
import axios from 'axios';
import { User } from '@esg/esg-global-types';
import { SoftDeleteUserClientData } from '@esg/syntiro-api-core';
import { MetadataError } from '@ep/error-handling';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as DeleteIcon } from '../../assets/icons/Delete.svg';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { CLOUD_FUNCTION_URL } from '../../env';
import AlertBanner from '../shared/banner/AlertBanner';
import { UserContext } from '../../context/UserContext';
import { auth } from '../../lib/google/firebase';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import PrimaryButton from '../shared/button/PrimaryButton';
import PrimaryDivider from '../shared/decoration/PrimaryDivider';
import { AuthHeader, getAuthHeaderForUser, handleAuthError } from '../../lib/auth/auth.util';
import { softDeleteUser } from '../../graphql/services/user.service';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';

interface PropTypes {
  delete_user: User | null;
  users: Array<User>;
  handleDeleteUserRow: (delete_user: User) => void;
  closeDrawer: () => void;
}

const DrawBoxContainer = styled(Box)({
  margin: 30
});

const DeleteUser = (props: PropTypes) => {
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [deletingUser, setDeletingUser] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  if (!group || !company) return null;

  const remaining_super_admin = props.users.find(
    (user) => user.super_admin && user.email !== user?.email
  );

  const navigate = useNavigate();

  const handleSignOut = () => {
    try {
      signOut(auth)
        .then(() => {
          localStorage.removeItem('current_group');
          localStorage.removeItem('current_company');
          // Sign-out successful.
          navigate('/');
        })
        .catch((err: unknown) => {
          const tracking_id: string = generateUuidV4();
          log(
            'error',
            new MetadataError(
              err instanceof Error
                ? err.message
                : 'Error: DeleteUser failed on an unknown error while initializing.',
              {
                user: user
              },
              tracking_id
            )
          );
          setFeedbackData({
            message: `Unable to sign out. Tracking ID: ${tracking_id}`,
            state: true,
            type: 'error'
          });
        });
      props.closeDrawer();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: DeleteUser failed on an unknown error while calling handleSignOut.',
          {
            user: user
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to sign out. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const handleDeleteUser = async (event: MouseEvent) => {
    try {
      if (props.delete_user) {
        event.preventDefault();
        setDeletingUser(true);
        setErrorMessage('');
        const auth_header: AuthHeader = await getAuthHeaderForUser();
        await axios({
          method: 'post',
          url: `${CLOUD_FUNCTION_URL}/deleteUser`,
          data: {
            id: props.delete_user.id,
            current_user_email: auth.currentUser?.email
          },
          headers: { ...auth_header }
        });
        const soft_delete_user_client_data: SoftDeleteUserClientData = {
          id: props.delete_user.id
        };
        await softDeleteUser(
          { group_id: group.id, company_id: company.id },
          soft_delete_user_client_data
        );
        props.closeDrawer();
        if (user?.email === props.delete_user.email) {
          handleSignOut();
        } else {
          props.handleDeleteUserRow(props.delete_user);
        }
      }
    } catch (err: unknown) {
      handleAuthError(err);
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: DeleteUser failed on an unknown error while calling handleDeleteUser.',
          {
            user: user,
            'props.delete_user': props.delete_user
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete user. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setDeletingUser(false);
    }
  };

  return (
    <Box>
      <DrawBoxContainer
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <DeleteIcon height={30} />
        <Typography variant="h6">Delete {props.delete_user?.email}</Typography>
      </DrawBoxContainer>
      <PrimaryDivider opacity={0.5} />
      <DrawBoxContainer>
        <AlertBanner
          severity="warning"
          message={
            'You are Deleting the Account currently in use. You will be logged out after this action'
          }
          open={user?.email === props.delete_user?.email}
        />
        <AlertBanner
          severity="error"
          message={'Cannot Delete only remaining Super Admin'}
          open={!remaining_super_admin && user?.email === props.delete_user?.email}
        />
        <PrimaryButton
          loading={deletingUser}
          fullWidth
          onClick={handleDeleteUser}
          sx={{ my: 4 }}
          disabled={!remaining_super_admin && user?.email === props.delete_user?.email}
        >
          Delete User
        </PrimaryButton>
        <Typography marginBottom={2} sx={{ color: 'red' }}>
          {errorMessage}
        </Typography>
      </DrawBoxContainer>
    </Box>
  );
};

export default DeleteUser;
